import { apiGet, apiGetPaginated, apiPatch, PaginatedResult } from '../client'
import { ConsultationMode } from './attorneyConnections'

export type FocusArea =
  | 'H1B_VISA'
  | 'L1_VISA'
  | 'O1_VISA'
  | 'EB1_GREEN_CARD'
  | 'EB2_GREEN_CARD'
  | 'EB3_GREEN_CARD'
  | 'NATIONAL_INTEREST_WAIVER'
  | 'OPT_STEM_OPT'
  | 'CPT'
  | 'TN_VISA'
  | 'J1_VISA'
  | 'F1_STUDENT_VISA'
  | 'ASYLUM_AND_REFUGEE'
  | 'NATURALIZATION_AND_CITIZENSHIP'
  | 'FAMILY_BASED_IMMIGRATION'
  | 'REMOVAL_AND_DEPORTATION_DEFENSE'
  | 'EMPLOYMENT_AUTHORIZATION'
  | 'PERM_LABOR_CERTIFICATION'
  | 'INVESTOR_VISA_EB5'
  | 'OTHER'

export type LawyerVerificationStatus = 'UNVERIFIED' | 'PENDING' | 'VERIFIED' | 'REJECTED'
export type AttorneyAgreementStatus = 'PENDING' | 'SENT' | 'SIGNED' | 'UPDATE_REQUIRED'

/** One bar admission: a state and the licence number there. */
export interface BarAdmission {
  licenseNumber: string
  state: string
}

export interface LawyerInstitution {
  city?: string | null
  logoUrl?: string | null
  name: string
  state?: string | null
  type?: string
}

export interface LawyerProfile {
  [key: string]: unknown
  agreementStatus?: AttorneyAgreementStatus
  availabilityNote?: string | null
  avatarUrl?: string | null
  barAdmissions?: BarAdmission[]
  barNumber?: string
  bio?: string
  consultationModes?: ConsultationMode[]
  consultationServices?: string[]
  firmWebsite?: string | null
  firstName: string
  focusAreas: FocusArea[]
  id: string
  institution?: LawyerInstitution
  jurisdictions: string[]
  languages?: string[]
  lastName: string
  lawFirmName: string
  licenseNumber: string
  licenseState?: string
  phoneNumber?: string
  schedulingUrl?: string | null
  specializations?: string[]
  verificationStatus: LawyerVerificationStatus
  yearsOfExperience?: number
}

export interface UpdateLawyerProfilePayload {
  availabilityNote?: string
  avatarUrl?: string
  barAdmissions?: BarAdmission[]
  barNumber?: string
  bio?: string
  consultationModes?: ConsultationMode[]
  consultationServices?: string[]
  firmWebsite?: string
  firstName?: string
  languages?: string[],
  lastName?: string,
  lawFirmName?: string,
  licenseNumber?: string,
  licenseState?: string,
  phoneNumber?: string
  schedulingUrl?: string
  yearsOfExperience?: number
}

/** The attorney's home: consultation counts, and where they stand on verification and the agreement. */
export interface LawyerDashboardStats {
  agreementStatus: AttorneyAgreementStatus
  completed: number
  networkOpen: boolean
  newRequests: number
  pending: number
  scheduled: number
  signingEnabled: boolean
  total: number
  upcoming: number
  verificationStatus: LawyerVerificationStatus
}

export interface PublicLawyerReview {
  comment?: string | null
  createdAt: string
  id: string
  raterRole: 'STUDENT' | 'EMPLOYER' | 'SCHOOL_ADMIN'
  stars: number
}

export interface PublicLawyerProfile {
  [key: string]: unknown
  avatarUrl?: string | null
  barAdmissions?: { state: string }[]
  bio?: string
  consultationModes?: ConsultationMode[]
  consultationServices?: string[]
  firmWebsite?: string | null
  firstName: string
  focusAreas: FocusArea[]
  id: string
  institution?: LawyerInstitution
  jurisdictions?: string[]
  languages?: string[]
  lastName: string
  lawFirmName: string
  rating?: { average: number | null; count: number }
  reviews?: PublicLawyerReview[]
  specializations?: string[]
  yearsOfExperience?: number
}

export const getLawyerProfile = () => apiGet<LawyerProfile>('/lawyers/me')

export const updateLawyerProfile = (payload: UpdateLawyerProfilePayload) => apiPatch<LawyerProfile>('/lawyers/me', payload)

export const getLawyerDashboardStats = () => apiGet<LawyerDashboardStats>('/lawyers/me/dashboard')

export const updateFocusAreas = (focusAreas: FocusArea[]) => apiPatch<LawyerProfile>('/lawyers/me/focus-areas', { focusAreas })

export const updateJurisdictions = (jurisdictions: string[]) => apiPatch<LawyerProfile>('/lawyers/me/jurisdictions', { jurisdictions })

export const updateSpecializations = (specializations: string[]) => apiPatch<LawyerProfile>('/lawyers/me/specializations', { specializations })

export const listPublicLawyers = (params?: { focusArea?: string; limit?: number; page?: number }): Promise<PaginatedResult<PublicLawyerProfile[]>> =>
  apiGetPaginated<PublicLawyerProfile[]>('/profiles/lawyers', { params })

export const getPublicLawyerProfile = (id: string) => apiGet<PublicLawyerProfile>(`/profiles/lawyers/${id}`)
